import React, { useState, FC } from 'react';
import { Modal, ModalBody, Row, Col, Button, Input, Label } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import { getMetaAppointmentsQuery } from 'vl-common/src/schemas/getMetaAppointments';
import { ModalProps } from 'reactstrap/types/lib/Modal';
import { useTranslation } from 'react-i18next';
import { CaseReviewApptTypes } from 'vl-common/src/hooks/useAppointmentManagmentActions';

type ChooseAppointmentTypeModalProps = {
  caseId: number;
  onSelectAppointmentType: (apptType: string, amendmentReason?: string, additionalInfo?: string) => void;
  onCancel: () => void;
} & ModalProps;

const ChooseAppointmentTypeModal: FC<ChooseAppointmentTypeModalProps> = ({
  caseId,
  onSelectAppointmentType,
  onCancel,
  ...restProps
}) => {
  const { t } = useTranslation('common');
  const [selectedOption, setSelectedOption] = useState('');
  const [amendmentReason, setAmendmentReason] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');

  const { data: appointmentsMeta } = useQuery({
    ...getMetaAppointmentsQuery({
      case_id: caseId
    })
  });

  const amendmentAppointmentReason = appointmentsMeta?.reopen_reasons || [];
  const optionsData = appointmentsMeta?.appt_types || [];

  const isAmendmentSelected = selectedOption === CaseReviewApptTypes.CASEREVADD;

  const isConfirmDisabled = !selectedOption || (isAmendmentSelected && !amendmentReason);

  const onConfirmSelection = () => {
    if (isConfirmDisabled) return;

    onSelectAppointmentType(
      selectedOption,
      isAmendmentSelected ? amendmentReason : undefined,
      isAmendmentSelected ? additionalInfo : undefined
    );
    onCancel();
  };

  const handleTypeChange = (val: string) => {
    setSelectedOption(val);
    setAmendmentReason('');
    setAdditionalInfo('');
  };

  return (
    <Modal size="lg" {...restProps} centered backdrop={'static'} keyboard={false}>
      <ModalBody>
        <Row style={styles.container}>
          <Col className="vl-flex-col-center">
            <div style={styles.title}>
              {t('Appointment')} <span style={styles.titleDarkSide}>{t('Type')}</span>
            </div>

            <div style={styles.subText}>{t('Please select appointment type')}</div>

            {/*@ts-expect-error*/}
            <Row style={styles.inputContainer} className="vl-flex-row-center">
              <Col xs="8" className="vl-filter-box">
                <Input
                  className="minimal"
                  type="select"
                  value={selectedOption}
                  data-testid="appointment-type-input"
                  onChange={(e) => handleTypeChange(e.target.value)}
                >
                  <option value="">{t('Select from dropdown')}</option>
                  {optionsData.map((item, i) => (
                    <option key={'mop' + i} value={item.appt_type_code}>
                      {item.appt_type_desc}
                    </option>
                  ))}
                </Input>
              </Col>
            </Row>

            {isAmendmentSelected && (
              <>
                <Row style={styles.conditionalContainer} className="vl-flex-row-center">
                  <Col xs="8">
                    {/*@ts-expect-error*/}
                    <Label style={styles.fieldLabel}>
                      {t('Reason for amendment')} <span className="vl-product-satsuma">*</span>
                    </Label>
                    <Input
                      type="select"
                      value={amendmentReason}
                      data-testid="amendment-reason-input"
                      onChange={(e) => setAmendmentReason(e.target.value)}
                    >
                      <option value="" disabled>
                        {t('Please select a reason')}
                      </option>
                      {amendmentAppointmentReason.map((item, i) => (
                        <option key={'mop' + i} value={item.reopen_code}>
                          {item.reopen_desc}
                        </option>
                      ))}
                    </Input>
                  </Col>
                </Row>

                <Row style={styles.conditionalContainer} className="vl-flex-row-center">
                  <Col xs="8">
                    {/*@ts-expect-error*/}
                    <Label style={styles.fieldLabel}>{t('Additional Information')}</Label>
                    <Input
                      type="textarea"
                      rows="4"
                      value={additionalInfo}
                      data-testid="amendment-additional-info-input"
                      onChange={(e) => setAdditionalInfo(e.target.value)}
                    />
                  </Col>
                </Row>
              </>
            )}

            <div className="vl-flex-row-center" style={styles.btnsContainer}>
              <Button
                style={styles.btnGray}
                type="button"
                data-testid="appointment-type-cancel-button"
                onClick={onCancel}
              >
                {t('Cancel')}
              </Button>
              <Button
                style={styles.btnMain}
                type="button"
                disabled={isConfirmDisabled}
                data-testid="appointment-type-confirm-button"
                onClick={onConfirmSelection}
              >
                {t('Confirm')}
              </Button>
            </div>
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
};

export default ChooseAppointmentTypeModal;

const styles = {
  container: { padding: '30px' },
  btnMain: {
    width: '110px',
    backgroundColor: 'var(--productPrimary)',
    fontSize: '14px',
    padding: '4px 10px',
    borderColor: 'var(--productPrimary)',
    color: 'var(--productPrimaryDark)'
  },
  btnGray: {
    width: '110px',
    backgroundColor: 'var(--productGray)',
    fontSize: '14px',
    padding: '4px 10px',
    borderColor: 'var(--productGray)',
    marginRight: '10px'
  },
  subText: {
    color: 'var(--productPrimaryDark)',
    fontSize: '14px',
    marginTop: '30px',
    marginBottom: '30px'
  },
  btnsContainer: { marginBottom: '30px', marginTop: '30px', width: '100%' },
  inputContainer: { position: 'relative', width: '100%' },
  conditionalContainer: { width: '100%', marginTop: '20px' },
  fieldLabel: {
    width: '100%',
    textAlign: 'left',
    fontWeight: '500',
    color: '#636466',
    fontSize: '14px',
    marginBottom: '5px'
  },
  title: { color: 'var(--productPink)', fontSize: '30px', fontWeight: 'bold' },
  titleDarkSide: { color: 'var(--productPrimaryDark)', fontWeight: 'bold' },
  titleHolder: { fontSize: '30px' }
};
